import { useEffect, useState } from 'react';
import type { ChangeEvent, MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Button,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  Chip,
  Avatar,
  TextField,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import {
  doc,
  getDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useTenant } from '../../providers/TenantProvider';
import { trustedStripeCheckoutUrl } from '../../lib/redirects';
import { getMemberBookingCatalog } from '../../lib/bookingApi';
import type { Service, ServiceCategory, Member } from '../../lib/db/types';

interface ServiceWithId extends Service {
  id: string;
}

interface ServiceCategoryWithId extends ServiceCategory {
  id: string;
}

interface ProviderInfo {
  id: string;
  name: string;
  photoUrl?: string;
  bio?: string;
}

interface TimeSlot {
  startTime: string;
  available: boolean;
  providerId?: string;
}

interface MemberWithId extends Member {
  id: string;
}

type BookingStep = 'service' | 'provider' | 'datetime' | 'confirm';

interface LocationState {
  appointmentId?: string;
  serviceId?: string;
  providerId?: string;
}

export function MemberBookPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, tenantId, activeMembership } = useTenant();
  const theme = useTheme();
  const routeState = location.state as LocationState | null;
  const isRescheduling = Boolean(routeState?.appointmentId);

  // State
  const [member, setMember] = useState<MemberWithId | null>(null);
  const [services, setServices] = useState<ServiceWithId[]>([]);
  const [categories, setCategories] = useState<ServiceCategoryWithId[]>([]);
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Booking state
  const [currentStep, setCurrentStep] = useState<BookingStep>('service');
  const [selectedService, setSelectedService] = useState<ServiceWithId | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ProviderInfo | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedSlotProviderId, setSelectedSlotProviderId] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [_appointmentId, setAppointmentId] = useState<string>('');
  const [preselectApplied, setPreselectApplied] = useState(false);

  const providersForService = (service: ServiceWithId | null) => {
    if (!service) return [];
    return providers.filter((provider) => service.providerIds?.includes(provider.id));
  };

  const isProviderlessService = (service: ServiceWithId | null) =>
    service?.bookingMode === 'class_session';

  // Load member data and booking catalog
  useEffect(() => {
    if (!user || !tenantId) {
      setError('Please sign in to book an appointment');
      setLoading(false);
      return;
    }

    if (!activeMembership?.memberId) {
      setError('Member record not found. Please contact support.');
      setLoading(false);
      return;
    }
    const currentMemberId = activeMembership.memberId;

    const loadData = async () => {
      try {
        const memberDoc = await getDoc(doc(db, 'members', currentMemberId));
        if (!memberDoc.exists()) {
          setError('Member record not found. Please contact support.');
          setLoading(false);
          return;
        }

        const memberData = { id: memberDoc.id, ...memberDoc.data() } as MemberWithId;
        setMember(memberData);

        const catalog = await getMemberBookingCatalog(tenantId);
        setCategories(catalog.categories as ServiceCategoryWithId[]);
        const bookableServices = catalog.services.filter((service) =>
          service.bookingMode === 'class_session'
            ? (service.recurringClassTimes || []).some((time) => time.isActive !== false && time.days.length > 0)
            : catalog.providers.some((provider) => service.providerIds?.includes(provider.id))
        );
        setServices(bookableServices as ServiceWithId[]);
        setProviders(catalog.providers.map((provider) => ({ ...provider, bio: undefined })));
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load appointment information');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeMembership?.memberId, tenantId, user]);

  useEffect(() => {
    if (preselectApplied || !routeState?.serviceId || services.length === 0) return;
    const service = services.find((candidate) => candidate.id === routeState.serviceId);
    if (!service) return;

    setSelectedService(service);
    setCurrentStep(isProviderlessService(service) ? 'datetime' : 'provider');
    if (!routeState.providerId) {
      setPreselectApplied(true);
    }
  }, [preselectApplied, routeState?.providerId, routeState?.serviceId, services]);

  useEffect(() => {
    if (preselectApplied || !routeState?.providerId || !selectedService || providersForService(selectedService).length === 0) return;
    const provider = providersForService(selectedService).find((candidate) => candidate.id === routeState.providerId);
    if (!provider) return;

    setSelectedProvider(provider);
    setSelectedDate('');
    setSelectedTime('');
    setCurrentStep('datetime');
    setPreselectApplied(true);
  }, [preselectApplied, providers, routeState?.providerId, selectedService]);

  // Generate available time slots when date and provider are selected
  useEffect(() => {
    if (!selectedDate || !selectedService || (!selectedProvider && !isProviderlessService(selectedService))) return;

    const loadAvailability = async () => {
      try {
        const calculateAvailability = httpsCallable(functions, 'calculateAvailability');
        const result = await calculateAvailability({
          tenantId,
          serviceId: selectedService.id,
          ...(isProviderlessService(selectedService) ? {} : { providerId: selectedProvider!.id }),
          date: selectedDate,
        });
        const data = result.data as { slots: TimeSlot[] };
        setAvailableSlots((data.slots || []).filter((slot) => slot.available));
      } catch (err: any) {
        console.error('Error loading availability:', err);
        setAvailableSlots([]);
      }
    };

    loadAvailability();
  }, [selectedDate, selectedService, selectedProvider, tenantId]);

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours} hr`;
    return `${hours} hr ${remainingMinutes} min`;
  };

  const formatPrice = (price?: number, currency?: string): string => {
    if (price === undefined || price === null) return 'Free';
    const dollars = price / 100;
    const currencySymbol = currency === 'EUR' ? '€' : '$';
    return `${currencySymbol}${dollars.toFixed(2)}`;
  };

  const dateInputValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatSelectedDate = (dateValue: string): string => {
    const [year, month, day] = dateValue.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryName = (categoryId?: string): string | null => {
    if (!categoryId) return null;
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || null;
  };

  const handleSelectService = (service: ServiceWithId) => {
    setSelectedService(service);
    setSelectedProvider(null);
    setSelectedDate('');
    setSelectedTime('');
    setCurrentStep('provider');
  };

  const handleSelectProvider = (provider: ProviderInfo) => {
    setSelectedProvider(provider);
    setCurrentStep('datetime');
  };

  const handleSelectDateTime = (slot: TimeSlot) => {
    setSelectedTime(slot.startTime);
    setSelectedSlotProviderId(slot.providerId || selectedProvider?.id || '');
    setCurrentStep('confirm');
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'provider':
        setCurrentStep('service');
        setSelectedService(null);
        break;
      case 'datetime':
        setCurrentStep(isProviderlessService(selectedService) ? 'service' : 'provider');
        setSelectedProvider(null);
        setSelectedDate('');
        setSelectedTime('');
        setSelectedSlotProviderId('');
        break;
      case 'confirm':
        setCurrentStep('datetime');
        setSelectedTime('');
        break;
    }
  };

  const handleBookAppointment = async () => {
    if (!member || !selectedService || (!selectedProvider && !selectedSlotProviderId) || !selectedDate || !selectedTime) {
      setError('Please complete all steps');
      return;
    }

    try {
      setBookingInProgress(true);
      setError(null);

      // Calculate start and end times
      const startDateTime = new Date(`${selectedDate}T${selectedTime}`);

      const result = isRescheduling
        ? await httpsCallable(functions, 'rescheduleAppointment')({
            appointmentId: routeState?.appointmentId,
            newStartTime: startDateTime.toISOString(),
          })
        : await httpsCallable(functions, 'bookAppointment')({
            tenantId,
            serviceId: selectedService.id,
            providerId: selectedSlotProviderId || selectedProvider!.id,
            startTime: startDateTime.toISOString(),
            customerType: 'member',
            customerId: member.id,
            source: 'member_portal',
            notes: '',
          });

      const data = result.data as { appointmentId: string; publicAccessHash?: string; status: string; checkoutUrl?: string };
      setAppointmentId(data.appointmentId);
      if (data.checkoutUrl) {
        window.location.assign(trustedStripeCheckoutUrl(data.checkoutUrl));
        return;
      }
      setBookingComplete(true);
      setSuccess(isRescheduling ? 'Appointment rescheduled successfully!' : 'Appointment booked successfully!');
    } catch (err: any) {
      console.error('Error booking appointment:', err);
      setError(err.message || 'Failed to book appointment. Please try again.');
    } finally {
      setBookingInProgress(false);
    }
  };

  const getStepIndex = (step: BookingStep): number => {
    const steps: BookingStep[] = ['service', 'provider', 'datetime', 'confirm'];
    return steps.indexOf(step);
  };

  // Get today's date and max date (30 days from now)
  const today = dateInputValue(new Date());
  const maxDateValue = new Date();
  maxDateValue.setDate(maxDateValue.getDate() + 30);
  const maxDate = dateInputValue(maxDateValue);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !member) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (bookingComplete) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 4 }}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 4 },
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="h5" component="h1" fontWeight={600}>
              {isRescheduling ? 'Reschedule Appointment' : 'Book an Appointment'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome back, {member?.contact.firstName}
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CardContent>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {isRescheduling ? 'Appointment Rescheduled!' : 'Appointment Confirmed!'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {isRescheduling
                  ? `Your appointment has been successfully rescheduled. A confirmation email has been sent to ${member?.contact.email}.`
                  : `Your appointment has been successfully booked. A confirmation email has been sent to ${member?.contact.email}.`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/member/appointments')}
                >
                  View My Appointments
                </Button>
                {!isRescheduling && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setBookingComplete(false);
                      setCurrentStep('service');
                      setSelectedService(null);
                      setSelectedProvider(null);
                      setSelectedDate('');
                      setSelectedTime('');
                      setSelectedSlotProviderId('');
                      setSuccess(null);
                    }}
                  >
                    Book Another
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" component="h1" fontWeight={600}>
                {isRescheduling ? 'Reschedule Appointment' : 'Book an Appointment'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Welcome back, {member?.contact.firstName}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => navigate('/member/appointments')}
              startIcon={<EventIcon />}
            >
              My Appointments
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Stepper */}
        <Stepper
          activeStep={getStepIndex(currentStep)}
          sx={{ mb: 4, display: { xs: 'none', sm: 'flex' } }}
        >
          <Step>
            <StepLabel>Select Service</StepLabel>
          </Step>
          <Step>
            <StepLabel>Select Provider</StepLabel>
          </Step>
          <Step>
            <StepLabel>Choose Date & Time</StepLabel>
          </Step>
          <Step>
            <StepLabel>Confirm</StepLabel>
          </Step>
        </Stepper>

        {/* Mobile Step Indicator */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, display: { sm: 'none' } }}
        >
          Step {getStepIndex(currentStep) + 1} of 4:{' '}
          {currentStep === 'service' && 'Select Service'}
          {currentStep === 'provider' && 'Select Provider'}
          {currentStep === 'datetime' && 'Choose Date & Time'}
          {currentStep === 'confirm' && (isRescheduling ? 'Confirm Reschedule' : 'Confirm Appointment')}
        </Typography>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Back Button (except on first step) */}
        {currentStep !== 'service' && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
        )}

        {/* Step 1: Select Service */}
        {currentStep === 'service' && (
          <>
            <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
              Select a Service
            </Typography>

            {services.length === 0 && !error ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No services available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please check back later or contact us directly.
                  </Typography>
                </CardContent>
              </Card>
            ) : services.length > 0 ? (
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {services.map((service) => {
                  const categoryName = getCategoryName(service.categoryId);
                  return (
                    <Grid item xs={12} sm={6} lg={4} key={service.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.2s ease-in-out',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          overflow: 'visible',
                        }}
                        onClick={() => handleSelectService(service)}
                      >
                        {/* Color indicator strip at top */}
                        <Box
                          sx={{
                            height: 4,
                            bgcolor: service.color || theme.palette.primary.main,
                            borderRadius: '4px 4px 0 0',
                          }}
                        />

                        {/* Service Image */}
                        {service.imageUrl && (
                          <Box
                            component="img"
                            src={service.imageUrl}
                            alt={service.name}
                            sx={{
                              width: '100%',
                              height: { xs: 140, sm: 160 },
                              objectFit: 'cover',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        )}

                        <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 2.5 } }}>
                          {/* Category Badge */}
                          {categoryName && (
                            <Chip
                              label={categoryName}
                              size="small"
                              sx={{
                                mb: 1.5,
                                bgcolor: 'grey.100',
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                height: 24,
                              }}
                            />
                          )}

                          {/* Service Name */}
                          <Typography
                            variant="h6"
                            component="h3"
                            fontWeight={600}
                            sx={{
                              mb: 1,
                              fontSize: { xs: '1.1rem', sm: '1.25rem' },
                            }}
                          >
                            {service.name}
                          </Typography>

                          {/* Description */}
                          {service.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 2,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.5,
                              }}
                            >
                              {service.description}
                            </Typography>
                          )}

                          {/* Service Details */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: 'text.secondary',
                              }}
                            >
                              <AccessTimeIcon fontSize="small" />
                              <Typography variant="body2">
                                {formatDuration(service.duration)}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: 'text.secondary',
                              }}
                            >
                              <AttachMoneyIcon fontSize="small" />
                              <Typography variant="body2" fontWeight={500}>
                                {formatPrice(service.price, service.currency)}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Select Button */}
                          <Button
                            variant="contained"
                            fullWidth
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                              mt: 'auto',
                              textTransform: 'none',
                              fontWeight: 500,
                            }}
                            onClick={(e: MouseEvent<HTMLElement>) => {
                              e.stopPropagation();
                              handleSelectService(service);
                            }}
                          >
                            Select
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : null}
          </>
        )}

        {/* Step 2: Select Provider */}
        {currentStep === 'provider' && selectedService && (
          <>
            <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
              Select a Provider
            </Typography>

            {/* Service Summary */}
            <Card sx={{ mb: 4, borderLeft: 4, borderColor: selectedService.color || theme.palette.primary.main }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {selectedService.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <AccessTimeIcon fontSize="small" />
                    <Typography variant="body2">{formatDuration(selectedService.duration)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <AttachMoneyIcon fontSize="small" />
                    <Typography variant="body2" fontWeight={500}>
                      {formatPrice(selectedService.price, selectedService.currency)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {providersForService(selectedService).length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Service unavailable
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    This service is not available for online appointments right now.
                  </Typography>
                  <Button variant="outlined" onClick={handleBack}>
                    Back to Services
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {providersForService(selectedService).map((provider) => (
                  <Grid item xs={12} sm={6} lg={4} key={provider.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease-in-out',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      onClick={() => handleSelectProvider(provider)}
                    >
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          p: { xs: 2, sm: 3 },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                        }}
                      >
                        {/* Provider Avatar */}
                        <Avatar
                          src={provider.photoUrl}
                          alt={provider.name}
                          sx={{
                            width: { xs: 80, sm: 100 },
                            height: { xs: 80, sm: 100 },
                            mb: 2,
                            bgcolor: theme.palette.primary.main,
                            fontSize: { xs: '2rem', sm: '2.5rem' },
                          }}
                        >
                          {provider.name.charAt(0).toUpperCase()}
                        </Avatar>

                        {/* Provider Name */}
                        <Typography variant="h6" component="h3" fontWeight={600} sx={{ mb: 1 }}>
                          {provider.name}
                        </Typography>

                        <Box sx={{ flexGrow: 1 }} />

                        {/* Select Button */}
                        <Button
                          variant="contained"
                          fullWidth
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            mt: 'auto',
                            textTransform: 'none',
                            fontWeight: 500,
                          }}
                          onClick={(e: MouseEvent<HTMLElement>) => {
                            e.stopPropagation();
                            handleSelectProvider(provider);
                          }}
                        >
                          Select
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* Step 3: Select Date & Time */}
        {currentStep === 'datetime' && selectedService && (selectedProvider || isProviderlessService(selectedService)) && (
          <>
            <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
              Choose Date & Time
            </Typography>

            {/* Summary */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Service</Typography>
                    <Typography variant="body1" fontWeight={500}>{selectedService.name}</Typography>
                  </Box>
                  {!isProviderlessService(selectedService) && selectedProvider && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Provider</Typography>
                      <Typography variant="body1" fontWeight={500}>{selectedProvider.name}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                    <Typography variant="body1" fontWeight={500}>{formatDuration(selectedService.duration)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Date Selection */}
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Select a Date
            </Typography>
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSelectedDate(e.target.value)}
              inputProps={{ min: today, max: maxDate }}
              sx={{ mb: 4, minWidth: 200 }}
            />

            {/* Time Slots */}
            {selectedDate && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Available Times
                </Typography>
                <Grid container spacing={1}>
                  {availableSlots.map((slot) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={`${slot.providerId || slot.startTime}-${slot.startTime}`}>
                      <Button
                        variant={selectedTime === slot.startTime && selectedSlotProviderId === (slot.providerId || selectedProvider?.id || '') ? 'contained' : 'outlined'}
                        fullWidth
                        onClick={() => handleSelectDateTime(slot)}
                        startIcon={<CalendarIcon />}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box component="span">{slot.startTime}</Box>
                        </Box>
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </>
        )}

        {/* Step 4: Confirm */}
        {currentStep === 'confirm' && selectedService && (selectedProvider || selectedSlotProviderId) && selectedDate && selectedTime && (
          <>
            <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
              {isRescheduling ? 'Confirm Your New Time' : 'Confirm Your Appointment'}
            </Typography>

            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom>
                  Appointment Summary
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Service</Typography>
                    <Typography variant="body1" fontWeight={500}>{selectedService.name}</Typography>
                    {selectedService.description && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedService.description}
                      </Typography>
                    )}
                  </Box>

                  {!isProviderlessService(selectedService) && selectedProvider && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Provider</Typography>
                      <Typography variant="body1" fontWeight={500}>{selectedProvider.name}</Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="caption" color="text.secondary">Date & Time</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatSelectedDate(selectedDate)} at {selectedTime}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duration: {formatDuration(selectedService.duration)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Price</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatPrice(selectedService.price, selectedService.currency)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Your Information</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {member?.contact.firstName} {member?.contact.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member?.contact.email}
                    </Typography>
                    {member?.contact.phone && (
                      <Typography variant="body2" color="text.secondary">
                        {member?.contact.phone}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleBookAppointment}
                    disabled={bookingInProgress}
                    startIcon={bookingInProgress ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                  >
                    {bookingInProgress
                      ? isRescheduling ? 'Rescheduling...' : 'Scheduling...'
                      : isRescheduling ? 'Confirm Reschedule' : 'Confirm Appointment'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </Box>
  );
}

export default MemberBookPage;
